import React from 'react';
import { PageSkeletonProps } from '../../../types/DesignSystem/PageSkeleton';
import Skeleton from '../Skeleton/Skeleton';
import { CONTROL_HEIGHT } from '../controlSize';
import { LiveRegionWords } from '../liveRegion';

/**
 * PageSkeleton (§ 62) — a whole page's first-load state, in one line.
 *
 * Tier 3 of the loading architecture. Tier 1 is the `Skeleton` primitive — the
 * ONLY owner of the shimmer recipe (`skeletonGrammar` enforces that). Tier 2 is
 * a `loading` state inside each component data flows through (`Table`). This
 * tier composes the primitive into the shapes GMS pages actually are, so a page
 * never hand-rolls a hundred pulse divs (`ProductViewSkeleton`) or centers a
 * 300×300 lottie (`CompatioLoader`) again.
 *
 * **A preset that does not match its page is worse than no skeleton.** The whole
 * promise of tier 3 is that geometry holds the real heights so nothing jumps; a
 * `detail` skeleton in front of a card-and-table page shows five form fields that
 * never arrive, and every one of them jumps. `summary-list` was added 2026-08-27
 * for exactly that reason — Manage Domain was reaching for `detail` because it was
 * the nearer of two wrong answers. When a page's shape is not here, add it here.
 * `explorer` was added 2026-09-19 under that rule: the ISR Data Model tab had no
 * loading state at all, and none of the three existing presets is a two-column
 * workspace — `detail` would have drawn a form where a rail belongs.
 *
 * The contracts:
 *
 * - **One announced wait** (WCAG 4.1.3) — Table's exact pattern: a single
 *   sr-only `role="status"` carrying `label`, `aria-busy` on the root, and every
 *   shape under one `aria-hidden` wrapper. Fifteen rectangles are decoration;
 *   the wait is one fact.
 * - **Geometry holds the real heights** so nothing jumps when content lands:
 *   toolbar controls and detail fields are md controls (`CONTROL_HEIGHT.md`),
 *   the table band sits on `surface-sunken` like the real header. Both themes
 *   come free — the primitive's gradient stops are role tokens.
 * - **Deterministic widths.** Cell widths cycle a fixed ladder by index — a
 *   texture that reads as data, identical on every render. No `Math.random`:
 *   tests, SSR and resumed renders must all see the same bytes.
 * - **Motion is the primitive's** — the 1.8s sweep, gated by
 *   `prefers-reduced-motion` (WCAG 2.3.3) down to a static gradient.
 */

/** The width ladder table cells cycle through — varied enough to read as data. */
const CELL_WIDTHS = ['90%', '70%', '80%', '60%'];
const cellWidth = (row: number, col: number) => CELL_WIDTHS[(row + col) % CELL_WIDTHS.length];

/** One table row: 12-col grid, 5/3/2/2 — the proportions of a typical GMS list. */
const COL_SPANS = ['tw-col-span-5', 'tw-col-span-3', 'tw-col-span-2', 'tw-col-span-2'];

/**
 * The Admin child-list proportions: two wide text columns, a count, and a narrow
 * kebab column. Matches Manage Domain's declared 32/32/31/5 far better than the
 * generic ladder above, whose last column is four times too wide.
 */
const SUMMARY_COL_SPANS = ['tw-col-span-4', 'tw-col-span-4', 'tw-col-span-3', 'tw-col-span-1'];

const PageSkeleton = React.forwardRef<HTMLDivElement, PageSkeletonProps>(function PageSkeleton({
  preset,
  label = 'Loading page',
  rows = 5,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const baseId = dataTestId || 'page-skeleton';

  return (
    <div
      /* rest first — see the rule in Button.tsx. */
      {...rest}
      ref={ref}
      aria-busy="true"
      className={className}
      data-testid={baseId}
    >
      <span role="status" className="tw-sr-only" data-testid={`${baseId}-status`}>
        {/* § 396 — the region mounts with the page skeleton, so its words wait for it. */}
        <LiveRegionWords>{label}</LiveRegionWords>
      </span>
      <div
        aria-hidden="true"
        /* `explorer` fills its container instead of stacking page bands: its two
           columns are the whole content, and the rail has to reach the bottom or the
           border-r stops halfway down. */
        className={preset === 'explorer' ? 'tw-h-full' : 'tw-flex tw-flex-col tw-gap-5'}
      >
        {/* No breadcrumb for `explorer` — it renders INSIDE a shell whose header and
            tabs are already on screen and stay there through the fetch. A breadcrumb
            here would be a second one, under the real one. */}
        {preset !== 'explorer' && (
          <div data-testid={`${baseId}-breadcrumb`} className="tw-flex tw-items-center tw-gap-2">
            <Skeleton variant="text" width="64px" />
            <Skeleton variant="circle" width="6px" height="6px" className="tw-shrink-0" />
            <Skeleton variant="text" width="96px" />
          </div>
        )}

        {/* `summary-list` has neither a bare page title nor a pair of top-right
            actions — its card owns the title, and its one action sits in the toolbar.
            Drawing them anyway is what made the Manage Domain skeleton read as a
            different page from the one that arrived. */}
        {preset !== 'summary-list' && preset !== 'explorer' && (
          <div data-testid={`${baseId}-title`} className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            {/* h-5: the 20px line box a title-header renders in. */}
            <Skeleton variant="rect" width="192px" className="tw-h-5" />
            <div className="tw-flex tw-gap-2">
              <Skeleton variant="rect" width="88px" className={CONTROL_HEIGHT.md} />
              <Skeleton variant="rect" width="120px" className={CONTROL_HEIGHT.md} />
            </div>
          </div>
        )}

        {preset === 'explorer' ? (
          /*
            The two-column workspace: a 30% bordered rail of item rows beside a detail
            card. Geometry mirrors the real tab element for element — the rail's
            `w-[30%] border-r pe-2 py-2.5` are the live classes, not approximations —
            because a skeleton whose columns are a different width than the content
            makes the whole page slide sideways when the fetch lands.
          */
          <div className="tw-flex tw-h-full">
            <div
              data-testid={`${baseId}-rail`}
              className="tw-flex-grow tw-w-[30%] tw-max-h-full tw-py-2.5 tw-pe-2 tw-border-r tw-border-solid tw-border-edge tw-flex tw-flex-col tw-gap-2 tw-overflow-hidden"
            >
              {/* Rail heading + its one loud action. `h-5` is the 20px line box a
                  title-header renders in; the button is `sm`, the height it really is. */}
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <Skeleton variant="rect" width="120px" className="tw-h-5" />
                <Skeleton variant="rect" width="132px" className={CONTROL_HEIGHT.sm} />
              </div>

              {/* Search + the collapse toggle beside it. */}
              <div className="tw-flex tw-items-center tw-gap-2">
                <Skeleton variant="rect" className={`tw-flex-1 ${CONTROL_HEIGHT.md}`} />
                <Skeleton variant="rect" width="88px" className={CONTROL_HEIGHT.md} />
              </div>

              {/* The rows. Each is the real bordered card: a name line, a quieter type
                  line under it, and the kebab. `wave` walks the rail top to bottom
                  (§ 223), so it reads as one band rather than N pulsing boxes. */}
              {Array.from({ length: rows }, (_, row) => (
                <div
                  key={row}
                  data-testid={`${baseId}-rail-row-${row}`}
                  className="tw-rounded-ctl tw-border tw-border-edge tw-px-3 tw-py-2.5 tw-flex tw-items-start tw-justify-between tw-gap-2 tw-shrink-0"
                >
                  <div className="tw-flex tw-flex-col tw-gap-1.5 tw-flex-1 tw-min-w-0">
                    <Skeleton variant="text" width={row % 2 ? '58%' : '44%'} wave={row} />
                    <Skeleton variant="text" width="72%" wave={row} />
                  </div>
                  <Skeleton variant="rect" width="4px" className="tw-h-4 tw-shrink-0" wave={row} />
                </div>
              ))}
            </div>

            {/* The detail pane: one card, a heading, then label/field pairs two across
                — the Sub model details shape.

                `flex-grow w-[70%]`, NOT `flex-1`. The two are not interchangeable here
                and the difference inverted the whole layout: `flex-1` sets `flex-basis: 0`,
                so the rail's 30% was the only basis in the row and the remaining 70% of
                free space split EVENLY between two `grow: 1` children — a 65% rail beside
                a 35% detail, the mirror image of the page. Stating both widths, as the
                live layout does, leaves no free space to distribute. */}
            <div
              data-testid={`${baseId}-detail`}
              className="tw-flex-grow tw-w-[70%] tw-max-h-full tw-py-4 tw-ps-4 tw-bg-surface-sunken tw-overflow-hidden"
            >
              {/* The card is `surface` ON the sunken pane — the real detail is a white
                  card floating on the grey ground, not a card the colour of the page. */}
              <div className="tw-rounded-srf tw-border tw-border-edge tw-bg-surface tw-p-5 tw-flex tw-flex-col tw-gap-5">
                {/* h-6: the 24px line box heading-1 renders in. */}
                <Skeleton variant="rect" width="160px" className="tw-h-6" />
                {Array.from({ length: 3 }, (_, row) => (
                  <div key={row} className="tw-grid tw-grid-cols-2 tw-gap-4">
                    {[0, 1].map((col) => (
                      <div key={col} className="tw-flex tw-flex-col tw-gap-1.5">
                        <Skeleton variant="text" width={col ? '96px' : '120px'} wave={row} />
                        <Skeleton variant="rect" className={CONTROL_HEIGHT.md} wave={row} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : preset === 'summary-list' ? (
          <>
            {/*
              The summary card. Geometry mirrors the real one element for element —
              title line, then two label/value pairs — so it lands at the same height
              rather than at a guessed one. `surface-sunken` is the card's own ground.
            */}
            <div
              data-testid={`${baseId}-summary`}
              className="tw-rounded-srf tw-bg-surface-sunken tw-p-4"
            >
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                {/* h-6: the 24px line box heading-1 renders in. */}
                <Skeleton variant="rect" width="160px" className="tw-h-6" />
                {/* The edit affordance — a square sm control, not a wide button. */}
                <Skeleton variant="rect" width="28px" className={CONTROL_HEIGHT.sm} />
              </div>
              {[0, 1].map((field) => (
                <div key={field} className="tw-mt-4 tw-flex tw-flex-col tw-gap-1">
                  <Skeleton variant="text" width={field ? '64px' : '80px'} />
                  <Skeleton variant="text" width={field ? '96px' : '120px'} />
                </div>
              ))}
            </div>

            {/* Split, not stacked left: search on one side, the create action on the
                other. The `list` preset's two-controls-together shape is wrong here. */}
            <div data-testid={`${baseId}-toolbar`} className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <Skeleton variant="rect" width="256px" className={CONTROL_HEIGHT.md} />
              <Skeleton variant="rect" width="132px" className={CONTROL_HEIGHT.md} />
            </div>

            <div data-testid={`${baseId}-table`} className="tw-rounded-srf tw-border tw-border-edge tw-overflow-hidden">
              <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-bg-surface-sunken tw-px-4 tw-py-3">
                {SUMMARY_COL_SPANS.map((span, col) => (
                  <Skeleton key={col} variant="text" className={span} width="60%" />
                ))}
              </div>
              {Array.from({ length: rows }, (_, row) => (
                <div
                  key={row}
                  className="tw-grid tw-grid-cols-12 tw-gap-4 tw-px-4 tw-py-3.5 tw-border-t tw-border-edge"
                >
                  {/* `wave` — one shimmer band sweeps the table row by row (§ 223);
                      the header band above is phase 0, so rows trail it. */}
                  {SUMMARY_COL_SPANS.map((span, col) => (
                    <Skeleton key={col} variant="text" className={span} width={cellWidth(row, col)} wave={row + 1} />
                  ))}
                </div>
              ))}
            </div>

            {/* The page-size control and the "Showing 1–4 of 4" summary, bottom right.
                Omitting it left a gap that filled in after the fetch. */}
            <div data-testid={`${baseId}-pagination`} className="tw-flex tw-items-center tw-justify-end tw-gap-3">
              <Skeleton variant="text" width="40px" />
              <Skeleton variant="rect" width="84px" className={CONTROL_HEIGHT.sm} />
              <Skeleton variant="text" width="112px" />
            </div>
          </>
        ) : preset === 'list' ? (
          <>
            <div data-testid={`${baseId}-toolbar`} className="tw-flex tw-gap-2">
              <Skeleton variant="rect" width="256px" className={CONTROL_HEIGHT.md} />
              <Skeleton variant="rect" width="96px" className={CONTROL_HEIGHT.md} />
            </div>
            <div data-testid={`${baseId}-table`} className="tw-rounded-srf tw-border tw-border-edge tw-overflow-hidden">
              <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-bg-surface-sunken tw-px-4 tw-py-3">
                {COL_SPANS.map((span, col) => (
                  <Skeleton key={col} variant="text" className={span} width="60%" />
                ))}
              </div>
              {Array.from({ length: rows }, (_, row) => (
                <div
                  key={row}
                  className="tw-grid tw-grid-cols-12 tw-gap-4 tw-px-4 tw-py-3.5 tw-border-t tw-border-edge"
                >
                  {/* `wave` — see the summary preset: one band, row by row (§ 223). */}
                  {COL_SPANS.map((span, col) => (
                    <Skeleton key={col} variant="text" className={span} width={cellWidth(row, col)} wave={row + 1} />
                  ))}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div data-testid={`${baseId}-fields`} className="tw-flex tw-flex-col tw-gap-5 tw-max-w-xl">
            {Array.from({ length: rows }, (_, row) => (
              <div key={row} className="tw-flex tw-flex-col tw-gap-1.5">
                {/* Label over field, the AddEditView shape; alternate label widths.
                    `wave` walks the form top to bottom (§ 223). */}
                <Skeleton variant="text" width={row % 2 ? '96px' : '72px'} wave={row} />
                <Skeleton variant="rect" className={CONTROL_HEIGHT.md} wave={row} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default PageSkeleton;
